const PLAN_YEAR = 'year';
const BILLING_MONTHLY = 'monthly';
const BILLING_ANNUAL = 'annual';
const ACTIVATION_EXTERNAL = 'external';
const ACTIVATION_AMPLITUDE_EXPERIMENT = 'amplitude_experiment';
const PRICING_BLOCK_ID = 'pricing-block';
const SIGNUP_LINK_EXCLUDE_CLASS = 'js-no-signup-link-swap';
const SIGNUP_LINK_SELECTOR = [
	'a[href^="https://app.optery.com/signup"]',
	'a[href^="//app.optery.com/signup"]',
].join( ', ' );

const pricingRuntimeState = new WeakMap();

/**
 * Calculates the maximum height among given elements.
 *
 * @param {NodeListOf<Element>} elements - Elements to measure.
 * @return {number} Max height.
 */
const getMaxHeight = ( elements ) => {

	let maxHeight = 0;
	elements.forEach( ( element ) => {

		if ( element.offsetHeight > maxHeight ) {

			maxHeight = element.offsetHeight;

		}

	} );
	return maxHeight;

};

/**
 * Resets the height style of the given elements to default.
 *
 * @param {NodeListOf<Element>} elements - The elements for which the height will be reset.
 */
const resetHeight = ( elements ) => {

	elements.forEach( ( element ) => {

		element.style.height = null;

	} );

};

/**
 * Sets a specified height for each element in the provided NodeList.
 *
 * @param {NodeListOf<Element>} elements - The collection of elements to adjust the height.
 * @param {number} height - The new height to set on each element, in pixels.
 */
const setHeight = ( elements, height ) => {

	elements.forEach( ( element ) => {

		element.style.height = `${height}px`;

	} );

};

/**
 * Activates the clicked button within a set of buttons.
 *
 * @param {Event} e - The event object representing the click event.
 * @param {NodeListOf<Element>} buttons - A collection of button elements within the same pricing block.
 */
const activeButton = ( e, buttons ) => {

	buttons.forEach( ( btn ) => {

		btn.classList.remove( 'active' );

	} );

	e.currentTarget.classList.add( 'active' );

};

/**
 * Handles click event on pricing switch buttons.
 *
 * @param {Event} e - The event object.
 * @param {NodeListOf<Element>} buttons - All buttons within the same pricing block.
 * @param {Element} block - The parent element representing the entire pricing block.
 */
const activePlan = ( e, buttons, block ) => {

	activeButton( e, buttons );

	const plan = e.currentTarget.getAttribute( 'data-show-plan' );
	if ( plan ) {

		block.setAttribute( 'data-active-plan', plan );

	}

};

/**
 * Sets up a ResizeObserver on a given element to adjust heights of headers based on width changes.
 *
 * @param {Element} plansContainer - The container element to observe for size changes.
 * @param {NodeListOf<Element>} heads - NodeList of header elements to adjust.
 * @return {ResizeObserver} ResizeObserver instance.
 */
const setupResizeObserver = ( plansContainer, heads ) => {

	const resizeObserver = new ResizeObserver( ( entries ) => {

		for ( const entry of entries ) {

			if ( entry.target === plansContainer ) {

				const mediaQuery = window.matchMedia( '(min-width: 768px)' );
				resetHeight( heads );
				if ( mediaQuery.matches ) {

					const newHeight = getMaxHeight( heads );
					setHeight( heads, newHeight );

				}

			}

		}

	} );
	resizeObserver.observe( plansContainer );

	return resizeObserver;

};

/**
 * Returns runtime state for a pricing block instance.
 *
 * @param {Element} block - Pricing block.
 * @return {Object} Runtime state.
 */
const getRuntimeState = ( block ) => {

	const existingState = pricingRuntimeState.get( block );
	if ( existingState ) {

		return existingState;

	}

	const state = {
		switchListenerBound: false,
		resizeObserver: null,
		buyNowActivated: false,
		amplitudeListenerBound: false,
	};
	pricingRuntimeState.set( block, state );

	return state;

};

/**
 * Reads dynamic checkout enabled flag for block.
 *
 * @param {Element} block - Pricing block.
 * @return {boolean} True when enabled.
 */
const isDynamicCheckoutEnabled = ( block ) => block.dataset.dynamicCheckoutEnabled === 'true';

/**
 * Reads amplitude checkout enabled flag for block.
 *
 * @param {Element} block - Pricing block.
 * @return {boolean} True when enabled.
 */
const isAmplitudeCheckoutEnabled = ( block ) => block.dataset.amplitudeCheckoutEnabled === 'true';

/**
 * Reads global signup anchor replacement flag for block.
 *
 * @param {Element} block - Pricing block.
 * @return {boolean} True when enabled.
 */
const isDynamicSignupAnchorLinksEnabled = ( block ) => block.dataset.dynamicSignupAnchorLinks === 'true';

/**
 * Returns activation method for the block.
 *
 * @param {Element} block - Pricing block.
 * @return {string} Activation method.
 */
const getActivationMethod = ( block ) => block.dataset.buyNowActivationMethod || 'immediate';

/**
 * Returns Amplitude Experiment flag key for the block.
 *
 * @param {Element} block - Pricing block.
 * @return {string} Experiment flag key.
 */
const getAmplitudeExperimentFlagKey = ( block ) => (
	block.dataset.amplitudeExperimentFlagKey ||
	''
).trim();

/**
 * Returns Amplitude Experiment variant value that activates the block.
 *
 * @param {Element} block - Pricing block.
 * @return {string} Variant value.
 */
const getAmplitudeExperimentVariantValue = ( block ) => (
	block.dataset.amplitudeExperimentVariantValue ||
	''
).trim();

/**
 * Returns true when runtime is production.
 *
 * @return {boolean} True in production runtime.
 */
const isProductionRuntime = () => {

	const environment = window.opteryAmplitude?.environment?.trim().toUpperCase();
	if ( environment ) {

		return environment === 'PROD';

	}

	const hostname = window.location.hostname.trim().toLowerCase();
	return hostname === 'optery.com' || hostname === 'www.optery.com';

};

/**
 * Reads optional non-production Amplitude variant override from URL.
 *
 * Query params:
 * - amp_variant (required): forced variant value.
 * - amp_flag (optional): limits override to one experiment key.
 *
 * @param {string} flagKey - Experiment flag key configured on block.
 * @return {string|null} Forced variant value or null when no override.
 */
const getForcedAmplitudeVariantValue = ( flagKey ) => {

	if ( isProductionRuntime() ) {

		return null;

	}

	const searchParams = new URLSearchParams( window.location.search );
	const forcedVariantValue = searchParams.get( 'amp_variant' )?.trim();
	if ( !forcedVariantValue ) {

		return null;

	}

	const forcedFlagKey = searchParams.get( 'amp_flag' )?.trim();
	if ( forcedFlagKey && forcedFlagKey !== flagKey ) {

		return null;

	}

	return forcedVariantValue;

};

/**
 * Returns true when block has at least one buy now feature enabled.
 *
 * @param {Element} block - Pricing block.
 * @return {boolean} True when feature is enabled.
 */
const hasBuyNowFeatureEnabled = ( block ) => isDynamicCheckoutEnabled( block );

/**
 * Returns currently selected billing value for block.
 *
 * @param {Element} block - Pricing block.
 * @return {string} Billing value.
 */
const getCurrentBillingValue = ( block ) => {

	const activePlan = block.getAttribute( 'data-active-plan' );
	return activePlan === PLAN_YEAR ? BILLING_ANNUAL : BILLING_MONTHLY;

};

/**
 * Reads selected country code for dynamic checkout.
 *
 * @param {Element} block - Pricing block.
 * @return {string} Country code or empty string.
 */
const getCurrentCountryCode = ( block ) => {

	const configuredCountryCode = block?.dataset.dynamicCheckoutCountry?.trim().toLowerCase();
	return configuredCountryCode || '';

};

/**
 * Builds checkout URL by overriding plan, billing and optional country parameters.
 *
 * @param {string} baseUrl - Base checkout URL.
 * @param {string} planValue - Plan value.
 * @param {string} billingValue - Billing value.
 * @param {string} countryCode - Country code.
 * @return {string|null} Final URL or null when invalid.
 */
const buildCheckoutUrl = ( baseUrl, planValue, billingValue, countryCode ) => {

	const normalizedBaseUrl = baseUrl?.trim();
	if ( !normalizedBaseUrl ) {

		return null;

	}

	try {

		const parsedUrl = new URL( normalizedBaseUrl, window.location.origin );
		const searchParams = new URLSearchParams( parsedUrl.search );

		searchParams.set( 'plan', planValue );
		searchParams.set( 'billing', billingValue );
		if ( countryCode ) {

			searchParams.set( 'country', countryCode );

		}
		parsedUrl.search = searchParams.toString();

		const isAbsoluteUrl = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test( normalizedBaseUrl ) || normalizedBaseUrl.startsWith( '//' );
		if ( isAbsoluteUrl ) {

			return parsedUrl.toString();

		}

		return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;

	} catch {

		return null;

	}

};

/**
 * Returns eligible pricing top sections within block.
 *
 * @param {Element} block - Pricing block.
 * @return {Element[]} Eligible sections.
 */
const getEligiblePlanSections = ( block ) => {

	const sections = block.querySelectorAll( '.b-pricing-item__plan-header' );
	return Array.from( sections ).filter( ( section ) => section.dataset.dynamicCheckoutExcluded !== 'true' );

};

/**
 * Rebuilds checkout links for eligible plans in active block.
 *
 * @param {Element} block - Pricing block.
 */
const updateDynamicCheckoutLinks = ( block ) => {

	if ( !isDynamicCheckoutEnabled( block ) ) {

		return;

	}

	const baseCheckoutUrl = block.dataset.dynamicCheckoutUrl;
	if ( !baseCheckoutUrl?.trim() ) {

		return;

	}

	const billingValue = getCurrentBillingValue( block );
	const countryCode = getCurrentCountryCode( block );
	const dynamicCheckoutButtonLabel = block.dataset.dynamicCheckoutButtonLabel?.trim();
	block.querySelectorAll( '.b-pricing-item__plan-header' ).forEach( ( section ) => {

		section.classList.remove( 'is-dynamic-checkout-eligible' );

	} );

	getEligiblePlanSections( block ).forEach( ( section ) => {

		section.classList.add( 'is-dynamic-checkout-eligible' );

		const pricingItem = section.closest( '.b-pricing-item' );
		const planValue = pricingItem?.dataset.plan?.trim();

		if ( !planValue ) {

			return;

		}

		const checkoutUrl = buildCheckoutUrl( baseCheckoutUrl, planValue, billingValue, countryCode );
		if ( !checkoutUrl ) {

			return;

		}

		section.querySelectorAll( '.b-pricing-item__button' ).forEach( ( button ) => {

			button.setAttribute( 'href', checkoutUrl );
			if ( dynamicCheckoutButtonLabel ) {

				button.textContent = dynamicCheckoutButtonLabel;

			}

		} );

	} );

};

/**
 * Sends Amplitude event for eligible buy now click.
 *
 * @param {Element} block - Pricing block.
 * @param {Element} button - Clicked button.
 */
const trackAmplitudeBuyNow = ( block, button ) => {

	if ( !isAmplitudeCheckoutEnabled( block ) ) {

		return;

	}

	const eventName = block.dataset.amplitudeEventName?.trim();
	if ( !eventName ) {

		return;

	}

	const track = window?.amplitude?.track;
	if ( typeof track !== 'function' ) {

		return;

	}

	const section = button.closest( '.b-pricing-item__plan-header' );
	if ( !section || section.dataset.dynamicCheckoutExcluded === 'true' ) {

		return;

	}

	const pricingItem = section.closest( '.b-pricing-item' );
	const planValue = pricingItem?.dataset.plan?.trim();
	if ( !planValue ) {

		return;

	}

	const billingValue = getCurrentBillingValue( block );
	const countryCode = getCurrentCountryCode( block );

	track( eventName, {
		flow: 'buy_now',
		'plan_tier': planValue,
		'billing_cycle': billingValue,
		...( countryCode ? {
			country: countryCode,
		} : {} ),
		'cta_source': 'plan_card',
		'entry_url': window.location.href,
	} );

};

/**
 * Binds click delegation for Amplitude event tracking.
 *
 * @param {Element} block - Pricing block.
 */
const bindAmplitudeListener = ( block ) => {

	const state = getRuntimeState( block );
	if ( state.amplitudeListenerBound || !isAmplitudeCheckoutEnabled( block ) ) {

		return;

	}

	block.addEventListener( 'click', ( event ) => {

		const button = event.target.closest( '.b-pricing-item__button' );
		if ( !button || !block.contains( button ) ) {

			return;

		}

		trackAmplitudeBuyNow( block, button );

	} );

	state.amplitudeListenerBound = true;

};

/**
 * Returns initialized Amplitude Experiment client.
 *
 * @return {Promise<Object|null>} Experiment client.
 */
const getAmplitudeExperimentClient = () => (

	Promise.resolve().then( () => {

		const namespace = window.opteryAmplitude || {};
		if ( namespace.experimentClient ) {

			return namespace.experimentClient;

		}

		if (
			window.Experiment &&
			namespace.experimentDeploymentKey &&
			typeof window.Experiment.initializeWithAmplitudeAnalytics === 'function'
		) {

			namespace.experimentClient = window.Experiment.initializeWithAmplitudeAnalytics( namespace.experimentDeploymentKey );
			window.opteryAmplitude = namespace;
			return namespace.experimentClient;

		}

		return null;

	} )

);

/**
 * Ensures a global Amplitude Experiment fetch promise exists.
 *
 * @return {Promise<Object|null>} Promise resolving to experiment client or null.
 */
const ensureAmplitudeExperimentFetchPromise = () => {

	const namespace = window.opteryAmplitude || {};
	if ( namespace.experimentFetchPromise ) {

		return Promise.resolve( namespace.experimentFetchPromise )
			.then( () => namespace.experimentClient || null )
			.catch( () => null );

	}

	const analyticsInitPromise = namespace.analyticsInitPromise || Promise.resolve();
	namespace.experimentFetchPromise = Promise.resolve( analyticsInitPromise )
		.then( () => getAmplitudeExperimentClient() )
		.then( ( experimentClient ) => {

			if ( !experimentClient || typeof experimentClient.fetch !== 'function' ) {

				return null;

			}

			return experimentClient.fetch()
				.then( () => experimentClient );

		} )
		.catch( () => null );

	window.opteryAmplitude = namespace;

	return namespace.experimentFetchPromise;

};

/**
 * Activates buy now features for one pricing block.
 *
 * @param {Element} block - Pricing block.
 */
const activateBuyNowForBlock = ( block ) => {

	const state = getRuntimeState( block );
	state.buyNowActivated = true;

	updateDynamicCheckoutLinks( block );
	updateGlobalSignupLinks( block );
	bindAmplitudeListener( block );

};

/**
 * Ensures pricing block has stable anchor id.
 *
 * @param {Element} block - Pricing block.
 */
const ensurePricingBlockId = ( block ) => {

	if ( !block.id ) {

		block.id = PRICING_BLOCK_ID;

	}

};

/**
 * Returns true if url points to app.optery.com/signup (with optional trailing slash).
 *
 * @param {URL} parsedUrl - Parsed URL instance.
 * @return {boolean} True for signup target.
 */
const isSignupUrl = ( parsedUrl ) => {

	const normalizedHost = parsedUrl.hostname.toLowerCase();
	const normalizedPath = parsedUrl.pathname.replace( /\/+$/, '' );

	return normalizedHost === 'app.optery.com' && normalizedPath === '/signup';

};

/**
 * Rewrites eligible signup links to pricing block anchor while preserving query params.
 *
 * @param {Element} block - Pricing block.
 */
const updateGlobalSignupLinks = ( block ) => {

	if ( !isDynamicSignupAnchorLinksEnabled( block ) ) {

		return;

	}

	ensurePricingBlockId( block );

	const links = document.querySelectorAll( SIGNUP_LINK_SELECTOR );
	links.forEach( ( link ) => {

		if ( link.closest( `.${SIGNUP_LINK_EXCLUDE_CLASS}` ) ) {

			return;

		}

		if ( link.closest( '.b-pricing' ) ) {

			return;

		}

		const hrefValue = link.getAttribute( 'href' )?.trim();
		if ( !hrefValue ) {

			return;

		}

		let parsedUrl;
		try {

			parsedUrl = new URL( hrefValue, window.location.origin );

		} catch {

			return;

		}

		if ( !isSignupUrl( parsedUrl ) ) {

			return;

		}

		const queryString = parsedUrl.search || '';
		const anchorTarget = `#${PRICING_BLOCK_ID}`;
		link.setAttribute( 'href', `${queryString}${anchorTarget}` );

	} );

};

/**
 * Activates a block when its configured Amplitude Experiment variant matches.
 *
 * @param {Element} block - Pricing block.
 */
const activateAmplitudeExperimentBlock = ( block ) => {

	const flagKey = getAmplitudeExperimentFlagKey( block );
	const expectedVariantValue = getAmplitudeExperimentVariantValue( block );
	if ( !flagKey || !expectedVariantValue ) {

		return;

	}

	const forcedVariantValue = getForcedAmplitudeVariantValue( flagKey );
	if ( forcedVariantValue ) {

		if ( forcedVariantValue === expectedVariantValue ) {

			activateBuyNowForBlock( block );

		}
		return;

	}

	ensureAmplitudeExperimentFetchPromise()
		.then( ( experimentClient ) => {

			if ( !experimentClient || typeof experimentClient.variant !== 'function' ) {

				return null;

			}

			return experimentClient.variant( flagKey );

		} )
		.then( ( variant ) => {

			if ( variant?.value === expectedVariantValue ) {

				activateBuyNowForBlock( block );

			}

		} )
		.catch( () => null );

};

/**
 * Activates blocks configured for external activation.
 */
const activateExternalPricingBlocks = () => {

	document.querySelectorAll( '.b-pricing' ).forEach( ( block ) => {

		if ( !hasBuyNowFeatureEnabled( block ) ) {

			return;

		}

		if ( getActivationMethod( block ) !== ACTIVATION_EXTERNAL ) {

			return;

		}

		activateBuyNowForBlock( block );

	} );

};

/**
 * Returns global namespace for pricing runtime.
 *
 * @return {Object} Namespace object.
 */
const getGlobalPricingNamespace = () => {

	window.opteryPricing = window.opteryPricing || {};
	return window.opteryPricing;

};

/**
 * Initializes global external activation API.
 */
const initGlobalBuyNowApi = () => {

	const namespace = getGlobalPricingNamespace();

	/**
	 * Activates buy now for blocks configured for manual activation.
	 */
	namespace.enableBuyNow = () => {

		activateExternalPricingBlocks();

	};

	if ( namespace.activationRequested ) {

		activateExternalPricingBlocks();

	}

};

/**
 * Initializes one pricing block instance.
 *
 * @param {Element} block - Pricing block.
 */
const initPricingBlock = ( block ) => {

	ensurePricingBlockId( block );

	const state = getRuntimeState( block );
	const buttons = block.querySelectorAll( '.b-pricing__switch-btn' );

	if ( !state.switchListenerBound ) {

		buttons.forEach( ( button ) => {

			button.addEventListener( 'click', ( event ) => {

				activePlan( event, buttons, block );

				if ( state.buyNowActivated && isDynamicCheckoutEnabled( block ) ) {

					updateDynamicCheckoutLinks( block );

				}

			} );

		} );

		state.switchListenerBound = true;

	}

	const heads = block.querySelectorAll( '.b-pricing-item__plan-header' );
	const plansContainer = block.querySelector( '.b-pricing__plans' );
	const mediaQuery = window.matchMedia( '(min-width: 768px)' );

	if ( mediaQuery.matches ) {

		const newHeight = getMaxHeight( heads );
		setHeight( heads, newHeight );

	}

	if ( plansContainer && !state.resizeObserver ) {

		state.resizeObserver = setupResizeObserver( plansContainer, heads );

	}

	if ( !hasBuyNowFeatureEnabled( block ) ) {

		return;

	}

	const activationMethod = getActivationMethod( block );
	if ( activationMethod === ACTIVATION_EXTERNAL ) {

		return;

	}

	if ( activationMethod === ACTIVATION_AMPLITUDE_EXPERIMENT ) {

		activateAmplitudeExperimentBlock( block );
		return;

	}

	activateBuyNowForBlock( block );

};

/**
 * Initializes all pricing blocks on the page.
 */
const initPricing = () => {

	initGlobalBuyNowApi();

	document.querySelectorAll( '.b-pricing' ).forEach( ( block ) => {

		initPricingBlock( block );

	} );

};

if ( document.readyState === 'loading' ) {

	document.addEventListener( 'DOMContentLoaded', initPricing );

} else {

	initPricing();

}
